import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { AdminPanel } from "@/components/site/Admin";
import { FestiveHero, GlowPhoto } from "@/components/site/FestiveHero";
import { useSite, ReloginNeeded } from "@/context/SiteContext";
import { errorMessage } from "@/lib/api";

const HOME_BG = "linear-gradient(180deg, #FCE4EB 0%, #FFF8F5 62%, #FFFCF8 100%)";

function HeroAdmin({ s }) {
  const { saveSettings } = useSite();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ hero_headline: s.hero_headline, hero_dates: s.hero_dates, hero_line: s.hero_line });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.hero_headline.trim() || !f.hero_dates.trim() || !f.hero_line.trim()) return setError("Please fill in all three boxes.");
    setBusy(true);
    setError("");
    try {
      await saveSettings(f);
      setEditing(false);
      toast.success("Saved. Everyone now sees the new hero text.");
    } catch (err) {
      if (!(err instanceof ReloginNeeded)) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminPanel testId="hero-admin-panel">
      {!editing ? (
        <button type="button" className="of-btn-ghost" onClick={() => setEditing(true)} data-testid="hero-edit-button">
          <Pencil className="h-4 w-4" aria-hidden /> Edit hero text
        </button>
      ) : (
        <div className="grid gap-4">
          <div>
            <label htmlFor="admin-hero-headline" className="of-label">Headline</label>
            <input id="admin-hero-headline" className="of-input" value={f.hero_headline} onChange={set("hero_headline")} maxLength={120} data-testid="admin-hero-headline-input" />
          </div>
          <div>
            <label htmlFor="admin-hero-dates" className="of-label">Dates</label>
            <input id="admin-hero-dates" className="of-input" value={f.hero_dates} onChange={set("hero_dates")} maxLength={80} data-testid="admin-hero-dates-input" />
          </div>
          <div>
            <label htmlFor="admin-hero-line" className="of-label">Gentle line</label>
            <textarea id="admin-hero-line" rows={3} className="of-input py-3" value={f.hero_line} onChange={set("hero_line")} maxLength={240} data-testid="admin-hero-line-input" />
          </div>
          {error && <p className="of-error" role="alert" data-testid="hero-admin-error">{error}</p>}
          <div className="flex flex-wrap gap-3">
            <button type="button" className="of-btn-soft" onClick={save} disabled={busy} data-testid="hero-admin-save-button">{busy ? "Saving…" : "Save"}</button>
            <button type="button" className="of-btn-ghost" onClick={() => setEditing(false)} data-testid="hero-admin-cancel-button">Cancel</button>
          </div>
        </div>
      )}
    </AdminPanel>
  );
}

export function Hero() {
  const { settings: s } = useSite();
  return (
    <FestiveHero labelledBy="hero-heading" bg={HOME_BG} banner testId="hero-section"
      photo={<GlowPhoto testId="hero-olivia-photo" iconTestId="hero-corner-icon" />}>
      <p className="of-eyebrow" data-testid="hero-tagline">Fighting Cancer for Kids</p>
      <p className="mt-3 text-base font-medium italic text-[#24453D]">You're welcome here. Take your time.</p>
      <h1 id="hero-heading" className="mt-3 text-4xl font-semibold leading-[1.02] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
        {s?.hero_headline ?? "15th Annual Holiday Toy Drive"}
      </h1>
      <p className="mt-5 inline-flex rounded-full bg-[#FFFCF8] px-4 py-2 text-sm font-semibold text-[#24453D] shadow-sm ring-1 ring-[#F3AFC2] sm:text-base" data-testid="hero-dates">
        {s?.hero_dates ?? "November 23 – December 11, 2026"}
      </p>
      <p className="mt-4 max-w-xl text-lg leading-relaxed text-[#2B3833] sm:text-xl" data-testid="hero-line">
        {s?.hero_line ?? "Every gift helps a child in treatment wake up to a little holiday magic in the hospital."}
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <a href="#give" className="of-btn-donate min-h-[52px] px-7 text-lg" data-testid="hero-give-button">Donate to the toy drive</a>
        <a href="#how" className="of-btn-ghost" data-testid="hero-how-button">See how it works</a>
      </div>
      {s && <HeroAdmin key={`${s.hero_headline}|${s.hero_dates}|${s.hero_line}`} s={s} />}
    </FestiveHero>
  );
}
